/** Edit / cancel an existing session (CS-02/CS-03/CS-05). Owner/manager edit
 *  freely; a trainer may only edit sessions they instruct (server re-checks via
 *  RLS). Cancel shows the affected booking + waitlist count before confirming. */
import type { schemas, types } from '@gym/shared';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card } from '../../components/ui.js';
import { useAuth } from '../../lib/auth.js';

import { useCancelClassSession, usePatchClassSession } from './hooks/useClassSessions.js';

export interface ClassSessionEditModalProps {
  session: types.ClassSessionDTO;
  onClose?: () => void;
}

export function ClassSessionEditModal({ session, onClose }: ClassSessionEditModalProps): ReactNode {
  const { t } = useTranslation();
  const { claims } = useAuth();
  const patch = usePatchClassSession();
  const cancel = useCancelClassSession();
  const [capacity, setCapacity] = useState(session.capacity);
  const [cancelCutoffAt, setCancelCutoffAt] = useState(session.cancelCutoffAt ?? '');
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const role = claims?.app_role;
  const isManager = role === 'owner' || role === 'manager';
  const isOwnSession = role === 'trainer' && session.instructorStaffId === claims?.staff_id;
  const canEdit = isManager || isOwnSession;

  async function save(): Promise<void> {
    setError(null);
    const input: schemas.ClassSessionPatchInput = {
      capacity,
      ...(cancelCutoffAt ? { cancelCutoffAt: new Date(cancelCutoffAt).toISOString() } : {}),
    };
    try {
      await patch.mutateAsync({ id: session.id, input });
      onClose?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function confirmCancel(): Promise<void> {
    setError(null);
    try {
      await cancel.mutateAsync({ id: session.id });
      onClose?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Card>
      <div
        role="dialog"
        aria-label={t('classes.calendar.title')}
        style={{ display: 'grid', gap: '0.75rem' }}
      >
        {!canEdit ? (
          <p data-testid="readonly-notice" style={{ color: 'var(--color-muted)' }}>
            {t('classes.sessionStatusScheduled')} · {session.bookedCount}/{session.capacity}
          </p>
        ) : confirmingCancel ? (
          <div role="alertdialog" aria-label={t('classes.session.cancel_confirm_title')}>
            <h3 style={{ marginTop: 0 }}>{t('classes.session.cancel_confirm_title')}</h3>
            <p>
              {t('classes.session.cancel_confirm_body', {
                bookedCount: session.bookedCount,
                waitlistCount: session.waitlistCount,
              })}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                variant="destructive"
                onClick={() => void confirmCancel()}
                disabled={cancel.isPending}
              >
                {t('common.confirm')}
              </Button>
              <Button variant="ghost" onClick={() => setConfirmingCancel(false)}>
                {t('common.back')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <label>
              {t('classes.capacity')}
              <input
                type="number"
                aria-label="capacity"
                value={capacity}
                onChange={(e) => setCapacity(Number(e.target.value))}
              />
            </label>
            <label>
              {t('classes.session.cancel_cutoff_at')}
              <input
                type="datetime-local"
                aria-label="cancelCutoffAt"
                value={cancelCutoffAt ? cancelCutoffAt.slice(0, 16) : ''}
                onChange={(e) => setCancelCutoffAt(e.target.value)}
              />
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button onClick={() => void save()} disabled={patch.isPending}>
                {t('common.save')}
              </Button>
              <Button variant="destructive" onClick={() => setConfirmingCancel(true)}>
                {t('classes.session.cancel_confirm_title')}
              </Button>
            </div>
          </>
        )}
        {error ? <p style={{ color: 'var(--color-danger)' }}>{error}</p> : null}
        {onClose ? (
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
